// src/components/ui/Sidebar.jsx
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useNavigate } from "react-router-dom"; // 👈 adăugat useNavigate
import Icon from "../AppIcon";
import Button from "./Button";
import { useAuth } from "../../auth/AuthProvider";

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState("management");
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const { roles, businesses, currentBusinessId, permissions } = useAuth();
  const navigate = useNavigate(); // 👈 instanțiat navigatorul

  const isPureEmployee = permissions?.isPureEmployee;

  const canSeeDashboard = Boolean(permissions?.canSeeDashboard);
  const canSeeClients = Boolean(permissions?.canSeeClients);
  const canSeeStaff = Boolean(permissions?.canSeeStaff);
  const canSeeServices = Boolean(permissions?.canSeeServices);
  const canSeeSms = Boolean(permissions?.canSeeSms);
  const canSeeSmsCampaigns = Boolean(permissions?.canSeeSmsCampaigns);
  const canSeeInventory = Boolean(permissions?.canSeeInventory);

  // helper: verificăm permisiunile pe baza codurilor de rol cerute
  const hasRole = (requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!permissions) return false;

    const {
      isSuperAdmin,
      isOwnerCurrentBusiness,
      isDirectorCurrentBusiness,
      isReceptionCurrentBusiness,
      isEmployeeCurrentBusiness,
    } = permissions;

    return requiredRoles.some((code) => {
      switch (code) {
        case "owner":
          return isSuperAdmin || isOwnerCurrentBusiness;
        case "director":
          return (
            isSuperAdmin ||
            isOwnerCurrentBusiness ||
            isDirectorCurrentBusiness
          );
        case "reception":
          return (
            isSuperAdmin ||
            isOwnerCurrentBusiness ||
            isDirectorCurrentBusiness ||
            isReceptionCurrentBusiness
          );
        case "employee":
          return (
            isSuperAdmin ||
            isOwnerCurrentBusiness ||
            isDirectorCurrentBusiness ||
            isReceptionCurrentBusiness ||
            isEmployeeCurrentBusiness
          );

        default:
          return false;
      }
    });
  };

  // meniul complet pentru roluri "mari"
  const fullNavigationItems = [
    {
      id: "dashboard",
      label: t("nav.dashboard"),
      path: "/app/admin-dashboard",
      icon: "LayoutDashboard",
      type: "single",
      requiredRoles: ["owner", "director"], // doar Owner & Director
    },
    {
      id: "management",
      label: t("nav.management"),
      icon: "Users",
      type: "group",
      children: [
        {
          id: "staff",
          label: t("nav.staff"),
          path: "/app/staff-management",
          icon: "UserCheck",
          requiredRoles: ["owner", "director"],
        },
        {
          id: "clients",
          label: t("nav.clients"),
          path: "/app/client-management",
          icon: "Users",
          requiredRoles: ["owner", "director", "reception"],
        },
        {
          id: "services",
          label: t("nav.services"),
          path: "/app/service-configuration",
          icon: "Settings",
          requiredRoles: ["owner", "director"],
        },
        {
          id: "inventory",
          label: t("nav.inventory"),
          path: "/app/inventory",
          icon: "Boxes",
          requiredRoles: ["owner", "director", "reception"],
        },
      ],
    },
    {
      id: "sms",
      label: t("nav.sms"),
      icon: "MessageSquare",
      type: "group",
      children: [
        {
          id: "sms_messages",
          label: t("nav.sms"),
          path: "/app/sms",
          icon: "MessageSquare",
          requiredRoles: ["owner", "director", "reception"],
        },
        {
          id: "sms_campaigns",
          label: t("nav.smsCampaigns"),
          path: "/app/sms-campaigns",
          icon: "Megaphone",
          requiredRoles: ["owner", "director", "reception"],
        },
      ],
    },
    {
      id: "settings",
      label: t("nav.settings"),
      icon: "Settings2",
      type: "group",
      children: [
        {
          id: "business_settings",
          label: t("nav.business"),
          path: "/app/business/settings",
          icon: "Building",
          requiredRoles: ["owner", "director"],
        },
        {
          id: "payment_settings",
          label: t("nav.payments"),
          path: "/app/settings/payments",
          icon: "CreditCard",
          requiredRoles: ["owner"],
        },
      ],
    },
    {
      id: "calendar",
      label: t("nav.calendar"),
      path: "/app/appointment-calendar",
      icon: "Calendar",
      type: "single",
      requiredRoles: ["employee"],
    },
  ];

  // meniul special pentru angajat "pur": doar calendar
  const employeeNavigationItems = [
    {
      id: "calendar",
      label: t("nav.calendar"),
      path: "/app/appointment-calendar",
      icon: "Calendar",
      type: "single",
      requiredRoles: ["employee"],
    },
  ];

  const navigationItems = isPureEmployee
    ? employeeNavigationItems
    : fullNavigationItems;

  // Filtrăm meniul în funcție de roluri
  const visibleNavigationItems = navigationItems
    .map((item) => {
      if (item.type === "single") {
        // plan + rol gating
        if (item.id === "dashboard" && !canSeeDashboard) return null;
        if (!item.requiredRoles || hasRole(item.requiredRoles)) return item;
        return null;
      }

      // group
      const visibleChildren = (item.children || []).filter((child) => {
        if (child.id === "clients" && !canSeeClients) return false;
        if (child.id === "staff" && !canSeeStaff) return false;
        if (child.id === "services" && !canSeeServices) return false;
        if (child.id === "sms_messages" && !canSeeSms) return false;
        if (child.id === "sms_campaigns" && !canSeeSmsCampaigns) return false;
        if (child.id === "inventory" && !canSeeInventory) return false;
        return !child.requiredRoles || hasRole(child.requiredRoles);
      });
      if (!visibleChildren.length) return null;
      return { ...item, children: visibleChildren };
    })
    .filter(Boolean);

  const handleSectionToggle = (sectionId) => {
    if (!isCollapsed)
      setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const currentBusiness = useMemo(
    () =>
      businesses.find((b) => String(b.id) === String(currentBusinessId)) ||
      null,
    [businesses, currentBusinessId]
  );

  // Mobile: toate grupurile (Management, SMS, etc.) intră sub "More"
  const mobileMoreItems = useMemo(() => {
    const groups = (visibleNavigationItems || []).filter(
      (i) => i?.type === "group"
    );

    const out = [];
    for (const g of groups) {
      for (const ch of g?.children || []) {
        out.push({ ...ch, _groupId: g.id });
      }
    }
    return out;
  }, [visibleNavigationItems]);

  const hasMobileMoreMenu = mobileMoreItems.length > 0;

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`fixed left-0 top-16 bottom-0 bg-surface border-r border-border transition-all duration-300 ease-out-custom z-100 ${
          isCollapsed ? "w-16" : "w-60"
        } hidden lg:block`}
      >
        {/* Toggle Button */}
        <div className="absolute -right-3 top-6 z-200">
          <Button
            variant="outline"
            size="icon"
            onClick={onToggle}
            className="w-6 h-6 bg-surface border border-border shadow-sm hover-elevate"
            type="button"
          >
            <Icon
              name={isCollapsed ? "ChevronRight" : "ChevronLeft"}
              size={14}
            />
          </Button>
        </div>

        {/* Navigation Content */}
        <div className="p-4 pt-8">
          <nav className="space-y-2">
            {visibleNavigationItems.map((item) => (
              <div key={item.id}>
                {item.type === "single" ? (
                  <Button
                    asChild
                    variant="ghost"
                    className="w-full justify-start px-3 py-2 h-10 transition-all duration-200 hover-elevate text-text-secondary hover:text-foreground hover:bg-muted data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=true]:shadow-sm"
                  >
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        isActive ? "data-[active=true]" : undefined
                      }
                    >
                      <span className="inline-flex items-center">
                        <Icon
                          name={item.icon}
                          size={18}
                          className="flex-shrink-0"
                        />
                        {!isCollapsed && (
                          <span className="ml-3 font-medium">
                            {item.label}
                          </span>
                        )}
                      </span>
                    </NavLink>
                  </Button>
                ) : (
                  <div>
                    <Button
                      variant="ghost"
                      onClick={() => handleSectionToggle(item.id)}
                      className="w-full justify-start px-3 py-2 h-10 text-text-secondary hover:text-foreground hover:bg-muted transition-all duration-200"
                      type="button"
                    >
                      <Icon
                        name={item.icon}
                        size={18}
                        className="flex-shrink-0"
                      />
                      {!isCollapsed && (
                        <>
                          <span className="ml-3 font-medium flex-1 text-left">
                            {item.label}
                          </span>
                          <Icon
                            name="ChevronDown"
                            size={16}
                            className={`transition-transform duration-200 ${
                              expandedSection === item.id ? "rotate-180" : ""
                            }`}
                          />
                        </>
                      )}
                    </Button>

                    {!isCollapsed && expandedSection === item.id && (
                      <div className="ml-6 mt-1 space-y-1 animate-slide-up">
                        {item.children?.map((child) => (
                          <Button
                            key={child.id}
                            asChild
                            variant="ghost"
                            className="w-full justify-start px-3 py-2 min-h-9 h-auto text-sm transition-all duration-200 hover-elevate text-text-secondary hover:text-foreground hover:bg-muted data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=true]:shadow-sm"
                          >
                            <NavLink
                              to={child.path}
                              className={({ isActive }) =>
                                isActive ? "data-[active=true]" : undefined
                              }
                            >
                              <span className="flex items-center min-w-0 w-full">
                                <Icon
                                  name={child.icon}
                                  size={16}
                                  className="flex-shrink-0"
                                />
                                <span className="ml-3 min-w-0 whitespace-normal break-words text-left">
                                  {child.label}
                                </span>
                              </span>
                            </NavLink>
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Bottom Section */}
        {!isCollapsed && (
          <div className="absolute bottom-4 left-4 right-4">
            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Building" size={16} color="white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground truncate">
                    {currentBusiness?.name || t("business.noBusiness")}
                  </div>
                  <div className="text-xs text-text-secondary truncate">
                    {roles && roles.length
                      ? roles.join(" · ")
                      : t("staff.noRole")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border z-100 lg:hidden">
        <div className="flex items-center justify-around py-2">
          {visibleNavigationItems
            .filter((item) => item.type === "single")
            .map((item) => (
              <Button
                key={item.id}
                asChild
                variant="ghost"
                className="flex flex-col items-center space-y-1 px-3 py-2 min-h-12 touch-target"
              >
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    isActive ? "text-primary" : "text-text-secondary"
                  }
                >
                  <div className="flex flex-col items-center">
                    <Icon name={item.icon} size={20} />
                    <span className="text-xs font-medium">{item.label}</span>
                  </div>
                </NavLink>
              </Button>
            ))}

          {/* More Menu for Mobile (grupuri: Management, SMS, etc.) */}
          {hasMobileMoreMenu && (
            <div className="relative">
              <Button
                variant="ghost"
                onClick={() => setMobileMoreOpen((v) => !v)}
                className="flex flex-col items-center space-y-1 px-3 py-2 min-h-12 touch-target text-text-secondary"
                type="button"
              >
                <Icon name="MoreHorizontal" size={20} />
                <span className="text-xs font-medium">{t("nav.more")}</span>
              </Button>

              {mobileMoreOpen && (
                <div
                  className="
                    fixed
                    top-1/2
                    right-0
                    -translate-y-1/2
                    flex
                    flex-col
                    items-end
                    gap-4
                    z-[110]
                  "
                >
                  {mobileMoreItems.map((child, index) => {
                    const colors = [
                      "bg-[#00ffffa6]",
                      "bg-[#ff9f43a6]",
                      "bg-[#ff4d4da6]",
                      "bg-[#5f27cfa6]",
                      "bg-[#10ac84a6]",
                    ];

                    const bgClass = colors[index % colors.length] || "bg-primary";

                    return (
                      <button
                        key={child.id}
                        type="button"
                        onClick={() => {
                          setMobileMoreOpen(false);
                          navigate(child.path);
                        }}
                        className={`
                          w-8 h-8 mr-1 rounded-sm
                          ${bgClass}
                          backdrop-blur-[20px]
                          text-primary-foreground
                          flex items-center justify-center
                          shadow-lg
                        `}
                      >
                        <Icon
                          name={child.icon}
                          size={16}
                          className="text-black"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
